import React, { useEffect, useState, useRef } from 'react';
// Import Shared Components
import { Table, Alert } from 'shared-components';
import { account } from 'shared-components/src/constants/propTypes';
// Import Global Utils
import { get } from '../../../../utils/http';
// Import Local Constant
import getFailedValueLoadColumns from './failedValueLoadColumns';
import { useTimeZone } from '../../../../hooks/useTimeZone';

function FailedValueLoads({ account }) {
  // useRef used here and on searchParams to prevent unnecessary running of useEffect
  const initialPaginationState = useRef({
    limit: 5,
    offset: 0,
    page: 0,
  });
  // initialFetched ensures the initial failed value load useEffect fetch runs only once despite having dependencies
  const initialFetched = useRef(false);
  const initialFailedValueLoads = { count: 0, data: [] };
  const [timeZone] = useTimeZone();

  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(null);
  // pagination params for failed value loads table
  const [pagination, setPagination] = useState(initialPaginationState.current);
  // state failedValueLoads is the response from list failed value loads
  const [failedValueLoads, setFailedValueLoads] = useState(initialFailedValueLoads);
  // Query params for ListFailedValueLoads that are not pagination related
  const searchParams = { account_id: account.id };

  // Run on startup and fetch all failed value loads for an account
  useEffect(() => {
    async function fetchInitialFailedValueLoads() {
      // Ensures that initial fetch does not run when either
      // - account does not exist
      // - initial already fetched
      if (!account || initialFetched.current) return;
      initialFetched.current = true;
      const queryParams = new URLSearchParams({
        ...initialPaginationState.current,
        ...searchParams,
      });
      try {
        setFailedValueLoads(
          await get(
            `/api/v1/client_portal/card_issuing/failed_value_loads?${queryParams}`
          )
        );
      } catch (e) {
        setIsError(e.message);
      }
      setIsLoading(false);
    }
    fetchInitialFailedValueLoads();
  }, [account, searchParams, initialPaginationState, initialFetched]); // all dependencies shouldn't change, so only run on startup

  // React Table related variables
  const defaultSorting = [{ id: 'id', desc: true }];
  const pages = Math.ceil(failedValueLoads.count / pagination.limit);
  const showPagination = failedValueLoads.count > pagination.limit;

  const handlePageChange = async pageIndex => {
    try {
      setIsLoading(true);
      setPagination({
        ...pagination,
        page: pageIndex,
        offset: pageIndex * pagination.limit,
      });
      const params = new URLSearchParams({
        ...searchParams,
        ...pagination,
        page: pageIndex,
        offset: pageIndex * pagination.limit,
      }).toString();
      setFailedValueLoads(
        await get(`/api/v1/client_portal/card_issuing/failed_value_loads?${params}`)
      );
    } catch (e) {
      setIsError(e.message);
    }
    setIsLoading(false);
  };

  const handlePageSizeChange = async (pageSize, pageIndex) => {
    try {
      setIsLoading(true);
      setPagination({
        ...pagination,
        limit: pageSize,
        offset: pageIndex * pageSize,
      });
      const params = new URLSearchParams({
        ...searchParams,
        ...pagination,
        limit: pageSize,
        offset: pageIndex * pageSize,
      }).toString();
      setFailedValueLoads(
        await get(`/api/v1/client_portal/card_issuing/failed_value_loads?${params}`)
      );
    } catch (e) {
      setIsError(e.message);
    }
    setIsLoading(false);
  };

  return (
    <>
      {isError ? (
        <Alert type="danger">{isError}</Alert>
      ) : (
        <Table
          columns={getFailedValueLoadColumns(timeZone)}
          data={failedValueLoads.data}
          defaultPageSize={5}
          defaultSorted={defaultSorting}
          loading={isLoading}
          loadingText="Loading..."
          manual
          noDataText="Currently there are no Failed Value Loads"
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          page={pagination.page}
          pages={pages}
          showPagination={showPagination}
          sortable={false}
        />
      )}
    </>
  );
}

FailedValueLoads.propTypes = {
  account,
};

FailedValueLoads.defaultProps = {
  account: undefined,
};

export default FailedValueLoads;
